/**
 * Generic Cache Manager
 *
 * Reusable cache infrastructure with:
 * - TTL-based expiration
 * - Stale-while-revalidate pattern
 * - Memory + localStorage hybrid caching
 * - ETag support for conditional requests
 *
 * Usage:
 * ```ts
 * const cache = new CacheManager<MyData>('my_cache_key', 10 * 60 * 1000)
 * cache.set(data, etag)
 * const cached = cache.get()
 * ```
 */

export interface CachedData<T> {
  data: T
  fetchedAt: number
  etag?: string
  version: number
}

export interface CacheOptions {
  /**
   * Time-to-live in milliseconds
   */
  ttl: number
  /**
   * localStorage key for persistence
   */
  storageKey: string
  /**
   * Cache version (increment to invalidate all caches)
   */
  version?: number
}

export class CacheManager<T> {
  private memoryCache: CachedData<T> | null = null
  private readonly options: Required<CacheOptions>

  constructor(options: CacheOptions) {
    this.options = {
      ...options,
      version: options.version ?? 1
    }
  }

  // ============================================================================
  // Cache Read Operations
  // ============================================================================

  /**
   * Get cached data (valid cache only)
   * Returns null if cache doesn't exist or is expired
   */
  get(): CachedData<T> | null {
    // Try memory cache first
    if (this.memoryCache && this.isValid(this.memoryCache)) {
      console.log(`[CacheManager:${this.options.storageKey}] Hit: memory cache`)
      return this.memoryCache
    }

    // Try localStorage
    try {
      const stored = localStorage.getItem(this.options.storageKey)
      if (!stored) return null

      const cached: CachedData<T> = JSON.parse(stored)

      // Check version compatibility
      if (cached.version !== this.options.version) {
        console.log(`[CacheManager:${this.options.storageKey}] Version mismatch, clearing cache`)
        this.clear()
        return null
      }

      // Check if valid
      if (!this.isValid(cached)) {
        console.log(`[CacheManager:${this.options.storageKey}] Cache expired`)
        this.clear()
        return null
      }

      // Hydrate memory cache
      this.memoryCache = cached
      console.log(`[CacheManager:${this.options.storageKey}] Hit: localStorage cache`)
      return cached
    } catch (error) {
      console.error(`[CacheManager:${this.options.storageKey}] Failed to read cache:`, error)
      return null
    }
  }

  /**
   * Get cached data even if stale (for stale-while-revalidate)
   */
  getStale(): CachedData<T> | null {
    // Try memory cache first
    if (this.memoryCache) {
      return this.memoryCache
    }

    // Try localStorage
    try {
      const stored = localStorage.getItem(this.options.storageKey)
      if (!stored) return null

      const cached: CachedData<T> = JSON.parse(stored)

      // Check version compatibility
      if (cached.version !== this.options.version) {
        console.log(`[CacheManager:${this.options.storageKey}] Version mismatch, clearing cache`)
        this.clear()
        return null
      }

      this.memoryCache = cached
      return cached
    } catch (error) {
      console.error(`[CacheManager:${this.options.storageKey}] Failed to read stale cache:`, error)
      return null
    }
  }

  /**
   * Get only the data (unwrapped)
   */
  getData(): T | null {
    const cached = this.get()
    return cached?.data || null
  }

  /**
   * Get ETag for conditional requests
   */
  getETag(): string | undefined {
    const cached = this.getStale()
    return cached?.etag
  }

  // ============================================================================
  // Cache Write Operations
  // ============================================================================

  /**
   * Save data to cache
   */
  set(data: T, etag?: string): void {
    try {
      const cached: CachedData<T> = {
        data,
        fetchedAt: Date.now(),
        etag,
        version: this.options.version
      }

      // Update memory cache
      this.memoryCache = cached

      // Update localStorage
      localStorage.setItem(this.options.storageKey, JSON.stringify(cached))

      console.log(`[CacheManager:${this.options.storageKey}] Cache updated`, {
        etag,
        ttl: `${this.options.ttl / 1000}s`
      })
    } catch (error) {
      console.error(`[CacheManager:${this.options.storageKey}] Failed to save cache:`, error)
    }
  }

  /**
   * Update data in cache (preserves ETag, resets TTL)
   */
  update(data: T): void {
    const cached = this.getStale()
    if (!cached) {
      // No existing cache, create new
      this.set(data)
      return
    }

    this.set(data, cached.etag)
  }

  /**
   * Touch cache (update fetchedAt for 304 Not Modified responses)
   */
  touch(etag?: string): void {
    const cached = this.getStale()
    if (!cached) return

    cached.fetchedAt = Date.now()
    if (etag) {
      cached.etag = etag
    }

    this.memoryCache = cached
    localStorage.setItem(this.options.storageKey, JSON.stringify(cached))

    console.log(`[CacheManager:${this.options.storageKey}] Cache touched (304 Not Modified)`)
  }

  // ============================================================================
  // Cache Invalidation
  // ============================================================================

  /**
   * Clear all cached data
   */
  clear(): void {
    this.memoryCache = null
    try {
      localStorage.removeItem(this.options.storageKey)
      console.log(`[CacheManager:${this.options.storageKey}] Cache cleared`)
    } catch (error) {
      console.error(`[CacheManager:${this.options.storageKey}] Failed to clear cache:`, error)
    }
  }

  /**
   * Invalidate cache (mark as stale without deleting)
   */
  invalidate(): void {
    const cached = this.getStale()
    if (!cached) return

    cached.fetchedAt = 0 // Force as expired
    this.memoryCache = cached
    localStorage.setItem(this.options.storageKey, JSON.stringify(cached))

    console.log(`[CacheManager:${this.options.storageKey}] Cache invalidated`)
  }

  // ============================================================================
  // Validation Helpers
  // ============================================================================

  /**
   * Check if cached data is still valid (not expired)
   */
  isValid(cached: CachedData<T>): boolean {
    const age = Date.now() - cached.fetchedAt
    return age < this.options.ttl
  }

  /**
   * Check if cache exists but is stale
   */
  isStale(): boolean {
    const cached = this.getStale()
    if (!cached) return false
    return !this.isValid(cached)
  }

  /**
   * Get cache age in seconds
   */
  getCacheAge(): number | null {
    const cached = this.getStale()
    if (!cached) return null
    return Math.floor((Date.now() - cached.fetchedAt) / 1000)
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    exists: boolean
    isValid: boolean
    isStale: boolean
    ageSeconds: number | null
    hasETag: boolean
  } {
    const cached = this.getStale()
    return {
      exists: !!cached,
      isValid: cached ? this.isValid(cached) : false,
      isStale: this.isStale(),
      ageSeconds: this.getCacheAge(),
      hasETag: !!cached?.etag
    }
  }
}
